import React, { useEffect, useMemo, useState } from 'react';
import Header from '../Header';
import { listReviewTypes, listPerformanceRatings } from '../../api/reviews';
import { listManagedKpiReviews, updateManagerReview } from '../../api/reviewsManaged';
import { upsertReview as upsertKpiReview } from '../../api/reviewsUpsert';
import KPIReviewRowManager from '../../components/reviews/KPIReviewRowManager';
import '../../components/reviews/reviews-table.css'; // custom look & feel

// Utility: group flat rows by employee then by KPA
function groupByEmployeeAndKpa(items) {
    // items: [{ employee:{id,name,email}, kpi:{..., kpa?}, review }]
    const empMap = new Map();
    for (const it of items) {
        const empId = it.employee.id;
        if (!empMap.has(empId)) {
            empMap.set(empId, { employee: it.employee, sections: new Map() /* kpaId -> {name, rows, kpaWeight, subtotal} */ });
        }
        const emp = empMap.get(empId);

        const kpaId = it.kpi?.kpa?.id ?? null;
        const kpaName = it.kpi?.kpa?.name ?? 'Ungrouped';
        const kpaWeight = it.kpi?.kpa?.weight ?? null; // if backend provides
        if (!emp.sections.has(kpaId)) {
            emp.sections.set(kpaId, { kpaId, kpaName, kpaWeight, rows: [], subtotal: 0 });
        }
        const sec = emp.sections.get(kpaId);
        sec.rows.push(it);
        sec.subtotal += Number(it.kpi?.weight ?? 0);
    }

    // Normalize & sort for rendering
    const employees = Array.from(empMap.values()).map(e => ({
        employee: e.employee,
        sections: Array.from(e.sections.values()).sort((a, b) => String(a.kpaName).localeCompare(String(b.kpaName))),
    }));
    return employees.sort((a, b) => String(a.employee.name).localeCompare(String(b.employee.name)));
}

export default function TeamPerformanceReviews() {
    // lookups
    const [reviewTypes, setReviewTypes] = useState([]);
    const [reviewTypeId, setReviewTypeId] = useState('');
    const [ratings, setRatings] = useState([]);

    // data
    const [rows, setRows] = useState([]); // flat from API
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState('');
    const [q, setQ] = useState(''); // filter by employee

    // collapsible state
    const [openEmployees, setOpenEmployees] = useState(() => new Set()); // employeeIds
    const [openKpas, setOpenKpas] = useState(() => new Set()); // keys: `${empId}-${kpaId}`

    // init lookups
    useEffect(() => {
        (async () => {
            try {
                const [rt, pr] = await Promise.all([listReviewTypes(), listPerformanceRatings()]);
                setReviewTypes(rt || []);
                setRatings(pr || []);
                const def = rt?.[0]?.id ? String(rt[0].id) : '';
                setReviewTypeId(def);
            } catch {
                setErr('Failed to initialize.');
            }
        })();
    }, []);

    // load data whenever review type changes
    useEffect(() => {
        if (!reviewTypeId) return;
        (async () => {
            setLoading(true); setErr('');
            try {
                const res = await listManagedKpiReviews(reviewTypeId);
                setRows(res.data || []);
                // default: open everything on load
                const emps = new Set((res.data || []).map(r => r.employee.id));
                setOpenEmployees(emps);
                const kpaKeys = new Set(
                    (res.data || []).map(r => `${r.employee.id}-${r.kpi?.kpa?.id ?? 'null'}`)
                );
                setOpenKpas(kpaKeys);
            } catch (e) {
                setErr(e?.response?.data?.message || 'Failed to load team reviews.');
            } finally {
                setLoading(false);
            }
        })();
    }, [reviewTypeId]);

    // Save handler (manager-only fields)
    const onSaveManagerRow = async ({ reviewId, kpiId, payload }) => {
        let id = reviewId;
        if (!id) {
            const created = await upsertKpiReview({
                reviewId: null,
                reviewTypeId,
                kpiId,
                body: payload,
            });
            id = created?.id ?? created?.review?.id ?? created?.data?.id ?? null;
        }
        if (!id) throw new Error('Unable to create review for manager update.');
        await updateManagerReview(id, payload);
        // refresh simple & safe
        const res = await listManagedKpiReviews(reviewTypeId);
        setRows(res.data || []);
    };

    // filter
    const filteredRows = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return rows;
        return rows.filter(r =>
            r.employee?.name?.toLowerCase().includes(term) ||
            r.employee?.email?.toLowerCase().includes(term)
        );
    }, [rows, q]);

    // grouped for rendering
    const employees = useMemo(() => groupByEmployeeAndKpa(filteredRows), [filteredRows]);

    // collapsible helpers
    const keyForKpa = (empId, kpaId) => `${empId}-${kpaId ?? 'null'}`;
    const isEmpOpen = (empId) => openEmployees.has(empId);
    const isKpaOpen = (empId, kpaId) => openKpas.has(keyForKpa(empId, kpaId));
    const toggleEmp = (empId) => {
        setOpenEmployees(prev => {
            const s = new Set(prev);
            if (s.has(empId)) {
                s.delete(empId);
                // also collapse all KPAs under this employee
                setOpenKpas(prevK => {
                    const nk = new Set(prevK);
                    Array.from(nk).forEach(k => { if (k.startsWith(`${empId}-`)) nk.delete(k); });
                    return nk;
                });
            } else {
                s.add(empId);
            }
            return s;
        });
    };
    const toggleKpa = (empId, kpaId) => {
        const key = keyForKpa(empId, kpaId);
        setOpenKpas(prev => {
            const s = new Set(prev);
            s.has(key) ? s.delete(key) : s.add(key);
            return s;
        });
    };
    const expandAll = () => {
        const allEmp = new Set(employees.map(e => e.employee.id));
        const allKpa = new Set();
        employees.forEach(e => e.sections.forEach(sec => allKpa.add(keyForKpa(e.employee.id, sec.kpaId))));
        setOpenEmployees(allEmp);
        setOpenKpas(allKpa);
    };
    const collapseAll = () => { setOpenEmployees(new Set()); setOpenKpas(new Set()); };
    const Caret = ({ open }) => <span className="me-2">{open ? '▾' : '▸'}</span>;

    return (
        <>
            <Header label={<p className="py-0 my-0">Team Performance Reviews</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">

                        <div className="card">
                            <div className="card-header d-flex align-items-center gap-3">
                                <div className="small text-muted">Review Type</div>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ maxWidth: 260 }}
                                    value={reviewTypeId}
                                    onChange={(e) => setReviewTypeId(e.target.value)}
                                >
                                    {reviewTypes.map(rt => (
                                        <option key={rt.id} value={rt.id}>{rt.name}</option>
                                    ))}
                                </select>

                                <div className="ms-auto d-flex gap-2">
                                    <input
                                        className="form-control form-control-sm"
                                        style={{ width: 280 }}
                                        placeholder="Filter by employee name or email…"
                                        value={q}
                                        onChange={(e) => setQ(e.target.value)}
                                    />
                                    <button className="btn btn-sm btn-outline-secondary" onClick={expandAll}>Expand all</button>
                                    <button className="btn btn-sm btn-outline-secondary" onClick={collapseAll}>Collapse all</button>
                                </div>
                            </div>

                            <div className="card-body">
                                {err && <div className="alert alert-danger">{err}</div>}
                                {loading ? (
                                    <div className="text-muted">Loading…</div>
                                ) : (
                                    <>
                                        {employees.length === 0 && <div className="text-muted">No records.</div>}

                                        {employees.map(emp => {
                                            const openEmp = isEmpOpen(emp.employee.id);
                                            return (
                                                <div className="mb-3 border rounded" key={emp.employee.id}>
                                                    {/* Employee header (toggle) */}
                                                    <button
                                                        type="button"
                                                        className="w-100 px-3 py-2 bg-light border-0 text-start d-flex justify-content-between align-items-center"
                                                        onClick={() => toggleEmp(emp.employee.id)}
                                                        title={openEmp ? 'Collapse employee' : 'Expand employee'}
                                                    >
                                                        <div className="d-flex align-items-center">
                                                            <Caret open={openEmp} />
                                                            <span className="fw-semibold">{emp.employee.name}</span>
                                                        </div>
                                                        <div className="small text-muted">{emp.employee.email}</div>
                                                    </button>

                                                    {/* Employee table */}
                                                    {openEmp && (
                                                        <div className="reviews-table">
                                                            <table>
                                                                <thead>
                                                                <tr className="rt-head-top">
                                                                    <th rowSpan={2} className="rt-counter">#</th>
                                                                    <th rowSpan={2} className="rt-name">Performance Review Discussion and Ratings</th>
                                                                    <th colSpan={3}>Acceptable / Expected / Exceeded Output</th>
                                                                    <th rowSpan={2}>Weight</th>
                                                                    <th rowSpan={2}>Actuals</th>
                                                                    <th colSpan={2}>Employee Evaluation</th>
                                                                    <th colSpan={2}>Employer Evaluation</th>
                                                                    <th rowSpan={2} className="rt-actions">Actions</th>
                                                                </tr>
                                                                <tr className="rt-head-bottom">
                                                                    <th>Threshold</th>
                                                                    <th>Target</th>
                                                                    <th>Stretch</th>
                                                                    <th>Rating (EE)</th>
                                                                    <th>Description</th>
                                                                    <th>Rating (ER)</th>
                                                                    <th>Description</th>
                                                                </tr>
                                                                </thead>

                                                                <tbody>
                                                                {emp.sections.map((sec, secIdx) => {
                                                                    const openSec = isKpaOpen(emp.employee.id, sec.kpaId);
                                                                    const kpaCounter = `KPA ${String(secIdx + 1).padStart(2, '0')}`;
                                                                    return (
                                                                        <React.Fragment key={`${emp.employee.id}-${String(sec.kpaId)}`}>
                                                                            {/* KPA header row with toggle (no subtotal) */}
                                                                            <tr className="rt-kpa-row">
                                                                                <td className="rt-counter">
                                                                                    <button
                                                                                        className="rt-kpa-toggle"
                                                                                        onClick={() => toggleKpa(emp.employee.id, sec.kpaId)}
                                                                                        title={openSec ? 'Collapse KPA' : 'Expand KPA'}
                                                                                    >
                                                                                        <Caret open={openSec} />
                                                                                        {kpaCounter}
                                                                                    </button>
                                                                                </td>
                                                                                <td className="rt-name">{sec.kpaName}</td>
                                                                                <td className="rt-num">—</td>
                                                                                <td className="rt-num">—</td>
                                                                                <td className="rt-num">—</td>
                                                                                <td className="rt-num">{sec.kpaWeight ?? '—'}</td>
                                                                                <td className="rt-num">—</td>
                                                                                <td colSpan={4}></td>
                                                                                <td className="rt-actions"></td>
                                                                            </tr>

                                                                            {/* KPI rows (collapsible) */}
                                                                            {openSec && sec.rows.map((row, kpiIdx) => (
                                                                                <KPIReviewRowManager
                                                                                    key={row.kpi.id}
                                                                                    kpi={row.kpi}
                                                                                    review={row.review}
                                                                                    ratings={ratings}
                                                                                    onSave={onSaveManagerRow}
                                                                                    counterText={`KPI ${String(kpiIdx + 1).padStart(2, '0')}`}
                                                                                />
                                                                            ))}
                                                                        </React.Fragment>
                                                                    );
                                                                })}

                                                                {emp.sections.length === 0 && (
                                                                    <tr><td colSpan={12} className="text-center text-muted py-4">No KPIs.</td></tr>
                                                                )}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>
                        </div>

                    </div>
                </div>
            </main>
        </>
    );
}
